import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { companyService } from '../../services/apiService';
import { JobSiteWithStats } from '../../store/interfaces';

const JobSiteStats: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user);
  const [companyId, setCompanyId] = useState<number>(2); // Default to company ID 2 since it has data
  const [jobSites, setJobSites] = useState<JobSiteWithStats[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);

  const fetchJobSiteStats = async () => {
    if (!user?.token) {
      setError('No token provided');
      return;
    }

    setLoading(true);
    setError(null);
    setTestResult(null);

    try {
      const result = await companyService.getCompanyJobSitesWithStats(user, companyId);
      setJobSites(result);
      setTestResult(`Success! Found ${result.length} job sites for company ${companyId}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setTestResult(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const listAllCompanies = async () => {
    if (!user?.token) {
      setError('No token provided');
      return;
    }

    try {
      const result = await companyService.getCompanies(user);
      setCompanies(result);
      setTestResult(`Found ${result.length} companies`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setTestResult(`Error listing companies: ${errorMessage}`);
    }
  };

  const clearData = () => {
    setJobSites([]);
    setError(null);
    setTestResult(null);
  };

  if (!user?.token) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong>Not logged in!</strong> Please log in first to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Job Site Analytics
          </h1>
          <p className="text-gray-600 text-lg">Real-time statistics and insights for your construction projects</p>
        </div>

        {/* Debug Info - Collapsible */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 mb-6">
          <details className="group">
            <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-800">🔧 Debug Information</h3>
              <span className="text-gray-500 group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <div className="p-4 border-t border-gray-200 bg-gray-50/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><strong>User:</strong> {user.email}</div>
                <div><strong>Role:</strong> <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">{user.role}</span></div>
                <div><strong>Token exists:</strong> <span className={user.token ? 'text-green-600' : 'text-red-600'}>{user.token ? '✅ Yes' : '❌ No'}</span></div>
                <div><strong>Token length:</strong> <span className="font-mono">{user.token?.length || 0}</span></div>
              </div>
            </div>
          </details>
        </div>

        {/* Controls */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 mb-6">
          <div className="p-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">📋 Data Controls</h3>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-64">
                <label htmlFor="companyId" className="block text-sm font-medium text-gray-700 mb-2">
                  Company ID
                </label>
                <input
                  id="companyId"
                  type="number"
                  value={companyId}
                  onChange={(e) => setCompanyId(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter Company ID"
                  min="1"
                />
              </div>
              <button
                onClick={fetchJobSiteStats}
                disabled={loading}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white px-8 py-3 rounded-xl font-medium transition-all transform hover:scale-105 disabled:transform-none shadow-lg"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading...
                  </span>
                ) : (
                  '🚀 Fetch Job Sites'
                )}
              </button>
              <button
                onClick={listAllCompanies}
                className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-xl font-medium transition-all transform hover:scale-105 shadow-lg"
              >
                📋 List Companies
              </button>
              <button
                onClick={clearData}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl font-medium transition-all transform hover:scale-105 shadow-lg"
              >
                🗑️ Clear Data
              </button>
            </div>
          </div>
        </div>

        {/* Test Result / Error */}
        {testResult && (
          <div className={`p-6 rounded-2xl mb-6 shadow-lg border ${testResult.includes('Error')
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-green-50 border-green-200 text-green-800'
            }`}>
            <div className="flex items-center">
              <span className="text-2xl mr-3">
                {testResult.includes('Error') ? '❌' : '✅'}
              </span>
              <div>
                <strong className="block mb-1">Status:</strong>
                <span className="text-sm">{testResult}</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 p-6 rounded-2xl mb-6 shadow-lg">
            <div className="flex items-center">
              <span className="text-2xl mr-3">❌</span>
              <div>
                <strong className="block mb-1">Error:</strong>
                <span className="text-sm">{error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Companies List */}
        {companies.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 mb-6 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-green-600 to-green-700">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <span className="mr-2">🏢</span>
                Available Companies
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {companies.map((company) => (
                  <div key={company.id} className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200 hover:shadow-md transition-all">
                    <div className="font-bold text-gray-900 text-lg">#{company.id}</div>
                    <div className="text-gray-600 font-medium">{company.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Job Sites Table */}
        {jobSites.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <span className="mr-2">📊</span>
                Job Sites for Company {companyId}
              </h3>
              <p className="text-blue-100 text-sm">Showing {jobSites.length} job sites</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      🏗️ Job Site
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      👥 Current Employees
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      👨‍💼 Foremen
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      💰 Active Cost Codes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/50 divide-y divide-gray-200">
                  {jobSites.map((jobSite, index) => (
                    <tr key={jobSite.id} className={`hover:bg-blue-50/80 transition-all duration-200 ${index % 2 === 0 ? 'bg-white/30' : 'bg-gray-50/30'}`}>
                      <td className="px-6 py-6 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">
                          {jobSite.name}
                        </div>
                      </td>
                      <td className="px-6 py-6 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {jobSite.currentEmployeeCount > 0 ? (
                            <>
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-800">
                                {jobSite.currentEmployeeCount} employees
                              </span>
                              {jobSite.currentEmployees.length > 0 && (
                                <div className="text-xs text-gray-500 mt-2 bg-gray-100 p-2 rounded-lg">
                                  {jobSite.currentEmployees.map(emp => emp.name).join(', ')}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                              no employees
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-6 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {jobSite.foremen.length > 0 ? (
                            <>
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-800">
                                {jobSite.foremen.length} foremen
                              </span>
                              <div className="text-xs text-gray-500 mt-2 bg-gray-100 p-2 rounded-lg">
                                {jobSite.foremen.map(foreman => foreman.name).join(', ')}
                              </div>
                            </>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                              no foremen
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-6 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {jobSite.activeCostCodes.length > 0 ? (
                            <>
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-purple-100 text-purple-800">
                                {jobSite.activeCostCodes.length} cost codes
                              </span>
                              <div className="text-xs text-gray-500 mt-2 bg-gray-100 p-2 rounded-lg">
                                {jobSite.activeCostCodes.map(code => code.name).join(', ')}
                              </div>
                            </>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                              no cost codes
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {jobSites.length === 0 && !loading && !error && testResult && !testResult.includes('Error') && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-6 rounded-2xl shadow-lg">
            <div className="flex items-center">
              <span className="text-2xl mr-3">⚠️</span>
              <div>
                <strong className="block mb-1">No job sites found</strong>
                <span className="text-sm">for company {companyId}. Try a different company ID.</span>
              </div>
            </div>
          </div>
        )}

        {/* Raw Data for Debugging - Collapsible */}
        {jobSites.length > 0 && (
          <div className="mt-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200">
            <details className="group">
              <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 rounded-t-2xl">
                <h3 className="text-lg font-semibold text-gray-800">🔍 Raw Data (for debugging)</h3>
                <span className="text-gray-500 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="p-4 border-t border-gray-200 bg-gray-50/50">
                <pre className="text-xs bg-white p-4 rounded-lg border overflow-auto max-h-96">
                  {JSON.stringify(jobSites, null, 2)}
                </pre>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobSiteStats; 